/*
 * Tickets (wallet) — `tickets.*` (App-shell canon §8; the white stub is
 * BASELINE-proposed §1.4, page FLEXIBLE).
 *
 * Transcribed from canon §8.1 (wallet: head, pager, white stub, wallet
 * buttons) + §8.2 (NOT ISSUED YET) + §1.3 DOM ids + the §8 testid table; the
 * DC mock (App Shell.dc.html passes view) is the styling reference. Canon
 * wins on conflict.
 *
 * DATA (Rulings A + C, BOARDROOM-2026-08-09-133534):
 *  - SHIPPED wallet renders ONLY live data — the real events the signed-in
 *    participant holds a place in, via the SAME source Get ready consumes
 *    (useLiveGetReadyEvents → server-derived readiness steps). Honest empty
 *    state when there are none. The canon 5-event [demo] dataset
 *    (src/data/seed.ts) is a TEST FIXTURE only and is never shipped.
 *  - Issuance is DERIVED (canon §2.4 binding rule): "a ticket is 'issued'
 *    only when every step is done." The pager (issued) / NOT-ISSUED-YET
 *    (pending) split reuses isIssued / nowStepCount from src/data/getReady.ts
 *    — no stored issued flag, no duplicated derivation, and a ticket a user
 *    is hunting for is never simply absent (§8.2 must-not).
 *
 * QR (canon §8 must-not: the DC's seeded-random genQR must NOT ship): the QR
 * is the REAL scannable payload — the opaque server-signed token from my-api
 * (services/my-api/src/qr.ts), rendered with the `qrcode` lib. The mint is
 * server-gated on get-ready readiness, so no token ⇒ an honest not-ready
 * state, never a fabricated code.
 */
import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { LiveDataNotice, useData } from '../data/DataProvider'
import { NotWired } from '../components/NotWired'
import { useShellChrome } from '../components/AppShell'
import { loadSession } from '../auth/session'
import { startSignIn } from '../auth/startSignIn'
import { formatBib } from '../data/bib'
import { formatEventDate } from './HeatSelect'
import { fetchActiveQr, mintQr, myApiBase } from '../data/myApi'
import {
  isIssued, nowStepCount,
  type GetReadyEvent, type GetReadyOrganiser,
} from '../data/getReady'
import { useLiveGetReadyEvents } from './GetReady'

// ---------------------------------------------------------------------------
// Wallet event shape — GetReadyEvent (the single source of truth) + stub
// display fields
// ---------------------------------------------------------------------------

/** Wallet-stub display fields (canon §8.1 — [chrome] labels, [demo]/live
 *  values). Unknown values render an honest em-dash, never an invention. */
export interface WalletStubFields {
  participantName: string
  ticketType: string
  /** Heat START TIME only, e.g. "07:30" (canon §8.1 HEAT column — the label
   *  is HEAT, not Wave, and the value is the start time, never the wave name). */
  heat: string
  /** Empty/absent ⇒ the Gate column is OMITTED (two-column footer, §8.1). */
  gate: string
  date: string
  /** Minted bib for display, already formatted ("001"). Absent/blank ⇒ the Bib
   *  column is OMITTED (pre-allocation), never a fabricated number.
   *  DESIGN-AUTO-BIB-ALLOCATION-V1 / BOARDROOM-2026-08-11-222211. */
  bib: string
}

export interface WalletEvent extends GetReadyEvent {
  organiser: GetReadyOrganiser & {
    /** Organiser band bg (stub header). Production comes from the central
     *  Trumin asset store per org-unit (canon §2.1/§8.1 must-not: never
     *  hand-built marks or per-event CSS); until that seam is wired the app
     *  accent token is the honest fallback. [demo] in fixtures. */
    accent?: string
  }
  stub?: Partial<WalletStubFields>
  /** Backing held ticket — the REAL QR token mints against it. Absent (e.g.
   *  fixture renders) ⇒ the QR block shows its honest unconfigured state. */
  ticketId?: string
}

// ---------------------------------------------------------------------------
// Derivations — REUSED from src/data/getReady.ts (canon §2.4; no duplicates)
// ---------------------------------------------------------------------------

/** Issued tickets for the pager: derived, every step incl. system done. */
export const issuedWalletEvents = <T extends GetReadyEvent>(events: T[]): T[] =>
  events.filter(isIssued)

/** NOT-ISSUED-YET cards — derived from the same source of truth as Get ready
 *  (canon §8.2): blocked (user still has `now` steps) or waiting (all yours
 *  done, system pending). Reason templates are verbatim [chrome] copy. */
export function pendingWalletReason(e: GetReadyEvent): { kind: 'blocked' | 'waiting'; text: string } {
  const todo = nowStepCount(e)
  if (todo > 0) {
    return { kind: 'blocked', text: `${todo} thing${todo > 1 ? 's' : ''} still to do before we can issue this` }
  }
  // DC derivation (App Shell.dc.html): waitingText = first pending system
  // step's meta, `Automatic · ` stripped.
  const waitingText = (e.steps.find(s => s.system && s.state === 'soon')?.meta ?? '').replace('Automatic · ', '')
  return { kind: 'waiting', text: `All done — we issue it at heat allocation (${waitingText})` }
}

// ---------------------------------------------------------------------------
// Real QR (preserved strength — WU-3 prior art, canon §8 must-not on genQR)
// ---------------------------------------------------------------------------

type QrLoadState = 'unconfigured' | 'loading' | 'ready' | 'not_ready' | 'unavailable'

function useEntryQr(ticketId?: string) {
  const [state, setState] = useState<QrLoadState>('unconfigured')
  const [dataUrl, setDataUrl] = useState<string | null>(null)

  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    if (!ticketId || !base || !session) {
      setState('unconfigured')
      return
    }
    let cancelled = false
    setState('loading')
    ;(async () => {
      // Read back the active token; only ISSUED tickets reach this pager, so
      // when none exists yet we ask the server to mint one — the mint stays
      // SERVER-gated on readiness (services/my-api), so a refusal is an
      // honest not-ready, never a client-fabricated code.
      let { token } = await fetchActiveQr(session.idToken, base, ticketId)
      if (!token) {
        try {
          ;({ token } = await mintQr(session.idToken, base, ticketId))
        } catch {
          token = null
        }
      }
      if (cancelled) return
      if (!token) {
        setState('not_ready')
        return
      }
      // Canon §8.1: centred QR, 116px.
      const url = await QRCode.toDataURL(token, { margin: 1, width: 116 })
      if (!cancelled) {
        setDataUrl(url)
        setState('ready')
      }
    })().catch(() => { if (!cancelled) setState('unavailable') })
    return () => { cancelled = true }
  }, [ticketId])

  return { state, dataUrl }
}

function QrBlock({ ticketId }: { ticketId?: string }) {
  const { state, dataUrl } = useEntryQr(ticketId)
  if (state === 'ready' && dataUrl) {
    return <img src={dataUrl} alt="Entry QR code" width={116} height={116} />
  }
  const message =
    state === 'loading' ? 'Checking your entry code…'
      : state === 'not_ready' ? 'Your entry QR appears here once get-ready (profile, waiver, heat) is complete.'
        : state === 'unavailable' ? 'The QR service isn’t responding right now — try again shortly.'
          : 'The QR service is not connected in this build — nothing here is fabricated.'
  return (
    <div
      role="status"
      data-wired={state === 'ready' ? 'true' : 'false'}
      style={{ width: 116, minHeight: 116, display: 'grid', placeItems: 'center', textAlign: 'center', fontSize: 11, lineHeight: 1.45, color: 'var(--stub-sub)' }}
    >
      {message}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The white stub — BASELINE (proposed §1.4): pixel-strict DC transcription
// ---------------------------------------------------------------------------

const stubFooterLabel: CSSProperties = {
  color: 'var(--stub-footer-label)', fontWeight: 700, textTransform: 'uppercase',
  fontSize: 9, letterSpacing: '.06em',
}

/** Notch that reads as a cut-out on the perforation edge — a small disc in the
 *  page-frame colour, half-clipped by the stub's overflow:hidden (canon §8.1
 *  perforation "with edge notches"). Cosmetic only. */
const stubNotch: CSSProperties = {
  position: 'absolute', top: -8, width: 16, height: 16, borderRadius: '50%',
  background: 'var(--bg-frame)',
}

function WalletStub({ e }: { e: WalletEvent }) {
  const stub = e.stub ?? {}
  return (
    <div
      id="tickets.stub"
      data-testid="tickets-stub"
      style={{
        // [token] ticket stub: bg #fff, ink #16150f (light card on dark app).
        background: 'var(--stub-bg)', borderRadius: 'var(--r-stub)', overflow: 'hidden',
        color: 'var(--stub-ink)', boxShadow: '0 14px 40px rgba(0,0,0,.35)',
      }}
    >
      {/* Organiser band: bg = organiser accent. The organiser LOGO slot is
          data-driven from the central Trumin asset store per org-unit (DC source
          comment; §8 must-not: never hand-built marks / per-event CSS). That
          asset-store seam is not wired yet (GetReadyOrganiser carries no logo
          URL), so — rather than a bare label — the honest branding is the
          organiser NAME set as a tracked UPPERCASE wordmark on the accent band
          (BOARDROOM-2026-08-14-100859 bug #3: "no branding, just the text"). It
          is a real value, never a fabricated mark. Drop the real <img> logo into
          this slot when the asset seam lands. */}
      <div
        data-testid="tickets-stub-org"
        style={{ background: e.organiser.accent ?? 'var(--org-accent)', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 9 }}
      >
        <span
          data-testid="tickets-stub-org-name"
          style={{ color: '#fff', fontSize: 13, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase' }}
        >{e.organiser.name}</span>
      </div>
      {/* Thin light "ADMIT ONE" strip under the organiser band (canon §8.1). */}
      <div
        data-testid="tickets-stub-admit"
        className="mono"
        style={{ background: 'rgba(0,0,0,.04)', padding: '4px 16px', fontSize: 9, fontWeight: 700, letterSpacing: '.22em', color: 'var(--stub-footer-label)' }}
      >
        ADMIT ONE
      </div>
      <div style={{ padding: '14px 18px 4px' }}>
        {/* Verbatim [chrome] label (canon §8.1); name rendered UPPERCASE. */}
        <div style={{ fontSize: 10, color: 'var(--stub-label)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>Participant</div>
        <div data-testid="tickets-stub-name" style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.08, textTransform: 'uppercase' }}>
          {stub.participantName || '—'}
        </div>
        {/* Rule between the participant block and the event/type block. */}
        <div aria-hidden style={{ height: 1, background: 'var(--stub-perforation)', margin: '12px 0 11px' }} />
        {/* Event name bold on its own line + mono UPPERCASE type line (§8.1). */}
        <div data-testid="tickets-stub-event" style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
          {e.name}
        </div>
        <div data-testid="tickets-stub-type" className="mono" style={{ fontSize: 11.5, color: 'var(--stub-sub)', marginTop: 3, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          {stub.ticketType || '—'}
        </div>
        {/* HEAT | DATE columns with a vertical divider — Gate column OMITTED
            when the event has no gate (§8.1). */}
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 14, margin: '13px 0 15px', fontSize: 11 }}>
          <div data-testid="tickets-stub-heat" style={{ flex: 1 }}>
            <div style={stubFooterLabel}>Heat</div>
            <div style={{ fontWeight: 700 }}>{stub.heat || '—'}</div>
          </div>
          {/* Bib is ALWAYS present in the layout (BOARDROOM-2026-08-13-223623):
              the field must exist even before allocation so a minted bib drops
              straight in; renders '—' until box-office allocates it
              (DESIGN-AUTO-BIB-ALLOCATION-V1). */}
          <div aria-hidden style={{ width: 1, background: 'var(--stub-perforation)' }} />
          <div data-testid="tickets-stub-bib" style={{ flex: 1 }}>
            <div style={stubFooterLabel}>Bib</div>
            <div style={{ fontWeight: 700 }}>{stub.bib || '—'}</div>
          </div>
          {stub.gate ? (
            <>
              <div aria-hidden style={{ width: 1, background: 'var(--stub-perforation)' }} />
              <div data-testid="tickets-stub-gate" style={{ flex: 1 }}>
                <div style={stubFooterLabel}>Gate</div>
                <div style={{ fontWeight: 700 }}>{stub.gate}</div>
              </div>
            </>
          ) : null}
          <div aria-hidden style={{ width: 1, background: 'var(--stub-perforation)' }} />
          <div data-testid="tickets-stub-date" style={{ flex: 1, textAlign: 'right' }}>
            <div style={stubFooterLabel}>Date</div>
            <div style={{ fontWeight: 700 }}>{stub.date || '—'}</div>
          </div>
        </div>
      </div>
      {/* [token] dashed perforation rgba(0,0,0,.15) with edge notches (§8.1). */}
      <div
        data-testid="tickets-stub-perforation"
        aria-hidden
        style={{ position: 'relative', height: 0, margin: '0 16px', borderTop: '2px dashed var(--stub-perforation)' }}
      >
        <span data-testid="tickets-stub-notch" style={{ ...stubNotch, left: -24 }} />
        <span data-testid="tickets-stub-notch" style={{ ...stubNotch, right: -24 }} />
      </div>
      {/* Centred QR, 116px — BELOW the perforation (canon §8.1). REAL payload
          only (see QrBlock). */}
      <div data-testid="tickets-stub-qr" style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 18px' }}>
        <QrBlock ticketId={e.ticketId} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wallet column (pager + stub + wallet buttons + NOT ISSUED YET) — canon §8.
// Presentational: rendered by the live screen below; unit tests render it
// with the seed FIXTURE.
// ---------------------------------------------------------------------------

// Wallet-pass buttons are HIDDEN for launch (BOARDROOM-2026-08-13-223623):
// Apple/Google pass generation is not wired for Sunday (§12.5 TBD) — don't ship
// dead buttons. Flip to true when real .pkpass / Google Wallet passes land.
const WALLET_BUTTONS_ENABLED = false

const pagerBtnStyle: CSSProperties = {
  minWidth: 'var(--tap-min)', minHeight: 'var(--tap-min)', borderRadius: 12,
  border: '1px solid var(--hairline-12)', background: 'var(--surface)',
  color: 'var(--text)', fontSize: 18, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
}

export function TicketsWallet({ events, initialPassId }: {
  events: WalletEvent[]
  /** Deep-link seam (`/tickets/:passId`, canon §1.2): opens the pager on the
   *  issued ticket whose backing ticketId matches. */
  initialPassId?: string
}) {
  const navigate = useNavigate()
  const issued = issuedWalletEvents(events)
  const pending = events.filter(e => !isIssued(e))

  const [rawIdx, setRawIdx] = useState(() => {
    const at = initialPassId ? issued.findIndex(e => e.ticketId === initialPassId) : -1
    return at >= 0 ? at : 0
  })
  // Wraps around (DC: ((idx % n) + n) % n).
  const n = issued.length
  const idx = n > 0 ? ((rawIdx % n) + n) % n : 0
  const pass = issued[idx]

  return (
    <div style={{ maxWidth: 'var(--wallet-max)', margin: '0 auto' }}>
      {pass ? (
        <>
          {/* Pager: ‹ / › (44px) around event name + mono `TICKET {i} of {n}` */}
          <div id="tickets.pager" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button data-testid="tickets-pager-prev" aria-label="Previous ticket" onClick={() => setRawIdx(i => i - 1)} style={pagerBtnStyle}>‹</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{pass.name}</div>
              <div data-testid="tickets-pager-pos" className="mono" style={{ fontSize: 10, color: 'var(--text-faint)', letterSpacing: '.14em' }}>
                {`TICKET ${idx + 1} of ${n}`}
              </div>
            </div>
            <button data-testid="tickets-pager-next" aria-label="Next ticket" onClick={() => setRawIdx(i => i + 1)} style={pagerBtnStyle}>›</button>
          </div>

          {/* Exactly ONE stub (one QR) visible at a time (§8.2 acceptance). */}
          <WalletStub key={pass.id} e={pass} />

          {/* Wallet buttons — HIDDEN for launch (WALLET_BUTTONS_ENABLED=false,
              BOARDROOM-2026-08-13-223623): pass generation is not wired for
              Sunday, so we ship no dead buttons. When enabled they render inert
              (disabled), no fake action, never the banned tooltip (§11.9). */}
          {WALLET_BUTTONS_ENABLED && (
          <div id="tickets.wallet" style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button
              data-testid="tickets-wallet-apple"
              disabled
              aria-disabled="true"
              data-wired="false"
              style={{ flex: 1, minHeight: 'var(--tap-min)', border: 0, borderRadius: 11, background: '#eef1f0', color: '#16150f', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}
            >
              {' Apple Wallet'}
            </button>
            <button
              data-testid="tickets-wallet-google"
              disabled
              aria-disabled="true"
              data-wired="false"
              style={{ flex: 1, minHeight: 'var(--tap-min)', border: '1px solid var(--hairline-16)', borderRadius: 11, background: 'transparent', color: 'var(--text)', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}
            >
              Google Wallet
            </button>
          </div>
          )}
        </>
      ) : (
        // Honest empty wallet (Ruling A): no issued tickets ⇒ no stub, ever —
        // nothing demo-seeded. (Empty-state copy is TBD-UNSEEN in the canon.)
        <p data-testid="tickets-empty" className="secondary" style={{ fontSize: 13 }}>
          No issued tickets yet. A ticket appears here the moment every
          get-ready step for its event is done.
        </p>
      )}

      {/* NOT ISSUED YET — derived from the same single source of truth as Get
          ready, "so the ticket a user is hunting for is never simply absent"
          (canon §8.2). */}
      {pending.length > 0 && (
        <div id="tickets.pending" data-testid="tickets-pending">
          {/* Verbatim [chrome] kicker. */}
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--text-dim)', margin: '26px 0 9px' }}>
            NOT ISSUED YET
          </div>
          {pending.map(e => {
            const reason = pendingWalletReason(e)
            return (
              <div
                key={e.id}
                id={`tickets.pending.${e.id}`}
                data-testid="tickets-pending-card"
                data-event-id={e.id}
                style={{ background: 'var(--surface)', border: '1px dashed var(--hairline-16)', borderRadius: 14, padding: 14, marginBottom: 10 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {e.organiser.name && (
                      <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: e.organiser.accentFg, marginBottom: 3 }}>
                        {e.organiser.name}
                      </div>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>{e.name}</div>
                    {e.when && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{e.when}</div>}
                  </div>
                </div>
                <div
                  data-testid="tickets-pending-reason"
                  style={{
                    fontSize: 11.5, lineHeight: 1.45, marginTop: 7,
                    // [token] blocked → amber; waiting → muted (§8.2).
                    ...(reason.kind === 'blocked'
                      ? { color: 'var(--warn)', fontWeight: 700 }
                      : { color: 'var(--text-muted)' }),
                  }}
                >
                  {reason.text}
                </div>
                {reason.kind === 'blocked' && (
                  <button
                    data-testid="tickets-pending-cta"
                    onClick={() => navigate('/get-ready')}
                    style={{ width: '100%', border: 0, borderRadius: 10, minHeight: 'var(--tap-min)', marginTop: 11, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    Get ready
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live screen — real held events, server-derived issuance (Rulings A + C)
// ---------------------------------------------------------------------------

/** Honest signed-out / expired-session guard (audit D23, same split as D7/D11):
 *  a signed-out user is NEVER told the build is unwired — they get a sign-in
 *  prompt + affordance, distinct from the no-API-base build gap (NotWired) and
 *  the fetch-failure "not responding" state. Mirrors GetReady's SignInNotice. */
function SignInNotice() {
  const [busy, setBusy] = useState(false)
  return (
    <div
      role="status"
      data-testid="tickets-signin"
      style={{
        border: '1px solid var(--hairline-12)', borderRadius: 'var(--r-card, 16px)',
        padding: 20, marginTop: 8, maxWidth: 'var(--wallet-max)',
        color: 'var(--text-secondary)', background: 'var(--surface)', fontSize: 13,
      }}
    >
      <p style={{ margin: '0 0 12px' }}>Please sign in to see your tickets.</p>
      <button
        data-testid="tickets-signin-cta"
        onClick={() => { setBusy(true); void startSignIn().then(started => { if (!started) setBusy(false) }) }}
        disabled={busy}
        style={{
          minHeight: 'var(--tap-min)', borderRadius: 9, padding: '0 16px', border: 0,
          background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 800, cursor: 'pointer',
        }}
      >
        {busy ? 'Opening sign-in…' : 'Sign in'}
      </button>
    </div>
  )
}

export function Tickets() {
  const { liveState, tickets, eventById } = useData()
  // The participant on every wallet ticket is the SIGNED-IN account. Prefer the
  // box-office assignee name, else the signed-in profile NAME (given+family from
  // the verified ID-token claims — real data, not fabricated), and NEVER the
  // email — the ticket must show a name, not "DUNCAN.FRANCIS@…" (CMT-142738).
  const selfProfileName = (() => {
    const c = loadSession()?.claims as Record<string, unknown> | undefined
    const parts = [c?.['given_name'], c?.['family_name']].filter(
      (s): s is string => typeof s === 'string' && s.trim() !== '',
    )
    return parts.join(' ').trim()
  })()
  // SAME live source as Get ready (Ruling C): server-derived readiness steps
  // per held event — the issued/pending split above derives from these rows.
  const { state, events, ticketByEvent } = useLiveGetReadyEvents()
  const { showPageHead } = useShellChrome()
  const { passId } = useParams()

  const walletEvents: WalletEvent[] = events.map(e => {
    const ticketId = ticketByEvent[e.id]
    const t = tickets.find(x => x.id === ticketId)
    const ev = eventById(e.id)
    return {
      ...e,
      ticketId,
      // Organiser band bg comes from the central Trumin asset store per
      // org-unit in production (canon §2.1) — seam not wired, so `accent`
      // stays unset and the stub falls back to the app accent token.
      stub: {
        // The holder of every wallet event is the signed-in account; use the
        // box-office assignee name when known, else the signed-in profile name.
        // Email is NOT a fallback (CMT-142738 — a ticket must show a name);
        // '—' renders only when no name exists anywhere.
        participantName: t?.assignee?.name || selfProfileName || '',
        ticketType: t?.typeName || '',
        // HEAT column shows the start TIME only (canon §8.1), not the wave name.
        // PREFER the heat SELECTED in get-ready (my-api readiness selectedHeatTime,
        // CMT-121325) — the box-office `t.wave` carries no self-select selection,
        // so a heat Duncan chose in get-ready showed '—'. Fall back to the
        // box-office wave time when my-api has no selection (older build / system-
        // allocated event); '' (→ em-dash) only when neither source knows.
        heat: e.selectedHeatTime || t?.wave?.time || '',
        // No live gate source yet ⇒ Gate column honestly omitted (§8.1).
        gate: '',
        // DATE: prefer the box-office event's own display date; else map the
        // my-api readiness eventStartsAt (CMT-121325, now live on stage) so an
        // issued OnePulse ticket shows the event date instead of '—'
        // (BOARDROOM-2026-08-14-002428 bug_date). '' (→ em-dash) only when
        // neither source knows the date.
        date: ev?.when || (e.eventStartsAt ? formatEventDate(e.eventStartsAt) : ''),
        // Minted bib from the box-office ticket payload, formatted for display
        // ("001"); absent until settings mints it ⇒ Bib column omitted.
        bib: formatBib(t?.bib) ?? '',
      },
    }
  })

  return (
    <div id="tickets.root">
      {/* Desktop-only page head (§8.1) */}
      {showPageHead && (
        <div id="tickets.head" data-testid="tickets-head">
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 4px' }}>Tickets</h1>
          {/* Verbatim [chrome] sub (canon §8.1). */}
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 18px' }}>
            {'Issued tickets only — one at a time, so you scan the right one. Works offline.'}
          </p>
        </div>
      )}
      {state === 'signed-out' ? (
        // Honest sign-out copy — checked FIRST so a signed-out / expired user
        // is never shown the my-api NotWired "not connected in this build"
        // panel (audit D23); a 401/403 mid-session lands here too.
        <SignInNotice />
      ) : liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : state === 'unconfigured' ? (
        <NotWired service="The ticket issuance/readiness service (my-api)" />
      ) : state === 'loading' ? (
        <p className="secondary" style={{ fontSize: 13 }} role="status">Loading your tickets&hellip;</p>
      ) : state === 'unavailable' ? (
        <p className="secondary" role="alert" style={{ fontSize: 13 }}>
          The ticket service isn&rsquo;t responding right now &mdash; try again shortly.
        </p>
      ) : (
        <TicketsWallet events={walletEvents} initialPassId={passId} />
      )}
    </div>
  )
}
